import { createFileRoute, Outlet, Link, useParams, useRouterState, redirect } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { LayoutDashboard, Users, AlertTriangle, Upload, ClipboardPaste, Settings as Cog, Package, Share2 } from "lucide-react";
import { getWorkspaceById } from "@/lib/dashboard.functions";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/workspaces/$workspaceId")({
  beforeLoad: async ({ location }) => {
    if (typeof window === "undefined") return;
    const { data } = await supabase.auth.getSession();
    if (!data.session) throw redirect({ to: "/login", search: { redirect: location.href } as never });
  },
  component: WorkspaceLayout,
});

function WorkspaceLayout() {
  const { workspaceId } = useParams({ from: "/_authenticated/workspaces/$workspaceId" });
  const fetchWs = useServerFn(getWorkspaceById);
  const { data } = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => fetchWs({ data: { workspace_id: workspaceId } }),
  });
  const path = useRouterState({ select: (s) => s.location.pathname });
  const ws = data?.workspace;

  const tabs: Array<{ to: any; label: string; icon: any; exact?: boolean }> = [
    { to: "/workspaces/$workspaceId", label: "Dashboard", icon: LayoutDashboard, exact: true },
    { to: "/workspaces/$workspaceId/contacts", label: "Contacts", icon: Users },
    { to: "/workspaces/$workspaceId/second-tier", label: "2nd Tier Affiliates", icon: Share2 },
    { to: "/workspaces/$workspaceId/products", label: "Products", icon: Package },
    { to: "/workspaces/$workspaceId/anomalies", label: "Anomalies", icon: AlertTriangle },
    { to: "/workspaces/$workspaceId/imports", label: "Imports", icon: Upload },
    { to: "/workspaces/$workspaceId/paste", label: "Manual Entry", icon: ClipboardPaste },
    { to: "/workspaces/$workspaceId/settings", label: "Settings", icon: Cog },
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Workspace</div>
        <h1 className="text-2xl font-semibold tracking-tight">{ws?.program_name ?? "Loading…"}</h1>
      </div>
      <div className="border-b -mx-6 px-6 overflow-x-auto">
        <nav className="flex gap-1">
          {tabs.map((t) => {
            const href = (t.to as string).replace("$workspaceId", workspaceId);
            const isActive = t.exact ? path === href : path.startsWith(href);
            return (
              <Link
                key={t.to as string}
                to={t.to}
                params={{ workspaceId } as any}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 text-sm border-b-2 -mb-px whitespace-nowrap",
                  isActive ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                <t.icon className="h-4 w-4" />
                {t.label}
              </Link>
            );
          })}
        </nav>
      </div>
      <Outlet />
    </div>
  );
}
